<template>
  <section class="body">
    <!-- Progress bar -->
    <div
      v-if="loadingFormData"
      class="text-center mb-2"
    >
      <b-progress
        :value="loadingFormDataValue"
        show-progress
        class="m-5"
      />
    </div>

    <b-form
      v-else
      @submit.prevent="submit"
    >
      <b-form-row class="mb-2 justify-content-center">
        <DateSiteModal
          :form="form.date"
        />
        <SiteModal :form="form" />
        <TargetGroupSiteModal
          :field-data="form.target_group"
          @onGoToListTab="goToListTab = true"
        />
        <div class="columnwrapper mr-3">
          <b-dropdown
            class="w-100 btn-overflow"
            variant="outline-secondary"
            size="sm"
            text="Show columns"
          >
            <div class="checkboxwrapper">
              <b-form-checkbox        
                v-model="allColsSelected"
                aria-describedby="Show columns"
                aria-controls="Show columns"
                class="dropdownclass"
                @change="toggleAll"
              >
                <span class="aligntextmiddle">
                  {{ allColsSelected ? "Deselect all" : "Select all" }}
                </span>
              </b-form-checkbox>

              <b-form-checkbox-group
                v-model="selectedColumns"
                class="dropdownclass"
                stacked
              >
                <b-form-checkbox
                  v-for="option in form.columns.options"
                  :key="option.value"
                  :value="option.value"
                >
                  <span class="aligntextmiddle">{{ option.text }}</span>
                </b-form-checkbox>
              </b-form-checkbox-group>
            </div>
          </b-dropdown>
        </div>
      </b-form-row>
      <b-form-row class="mb-2 justify-content-center">
        <button
          v-if="getSelectedInputs > 0"
          type="button"
          class="trashbtn mt-2 mr-2"
          @click="clearAllFields"
        >
          <b-icon-trash />
        </button>
        <div
          v-for="(selectedButton, key) in selectedBtns"
          :key="key"
        >
          <button
            class="selectedbtns mr-2 mt-2 bubblefontsize"
            type="button"
            @click="selectedButton.clickedBtn && selectedButton.clickedBtn()"
          >
            <span v-html="selectedButton.text" />
            <b-icon
              v-if="selectedButton.editable"
              class="ml-2 crossbtn"
              font-scale="1.5"
              icon="x"
              @click="
                selectedButton.clickedBtnDelete &&
                  selectedButton.clickedBtnDelete($event)
              "
            />
          </button>
        </div>
      </b-form-row>
      <b-form-row class="mb-2 justify-content-center">
        <b-button
          :disabled="disableRunBtn"
          type="submit"
          class="searchbutton ml-2"
          variant="primary"
          size="sm"
        >
          <b-icon
            v-if="!loadingSearchResults"
            icon="arrow-repeat"
          />
          {{ loadingSearchResults ? 'Working...' : 'Run' }}
        </b-button>
      </b-form-row>
      <b-form-row
        v-if="getTargetGroupCount() === 0"
        class="mb-2 justify-content-center"
      >
        <div class="text-center mb-2">
          <small
            class="text-danger"
          ><strong>You must select a target group under target / metric</strong></small>
        </div>
      </b-form-row>
    </b-form>
    <!-- Results -->
    <template v-if="initFirstSearch">
      <!-- Show if loading results -->
      <div
        v-if="!hasData && loadingSearchResults"
        class="text-center mb-2"
      >
        <hr>
        <b-spinner
          variant="primary"
          type="grow"
          label="Spinning"
        />
        <div>
          <small class="text-muted">Loading search results...</small>
        </div>
      </div>

      <div>
        <!-- Show if search have hits -->
        <SiteResult
          v-show="hasData"
          :disabled="loadingSearchResults"
          :class="{ 'overlay-disabled': loadingSearchResults }"
          :go-to-list-tab="goToListTab"
          @sort-only="submitSort"
          @exclude-rows="handleExcludeRows"
          @resetGoToListTab="goToListTab = false"
        />

        <!-- Show if search have no hits -->
        <div
          v-if="!hasData && !loadingSearchResults"
          class="text-center"
        >
          <hr>
          <p>No search results</p>
        </div>
      </div>
    </template>
    <SlowQueryModal 
      :show="showSlowQueryModal" 
      :loading="loadingExportResults" 
      @download="handleExportResults"
      @cancel="showSlowQueryModal = false" 
    />
    <ScheduleReportModal 
      :show="showScheduleReportModal" 
      :loading="loadingScheduleReport" 
      @submit="handleScheduleReportSubmit" 
      @cancel="showScheduleReportModal = false" 
    />
  </section>
</template>

<script>
import SlowQueryModal from "../../Shared/SlowQueryModal/SlowQueryModal.vue"
import ScheduleReportModal from "../../Shared/ScheduleReportModal/ScheduleReportModal.vue"
import 'vue2-datepicker/index.css';
import DateSiteModal from '../Site/DateSiteModal';
import SiteModal from '@/components/Shared/SiteModal';
import TargetGroupSiteModal from '../Site/TargetGroupSiteModal';
import SiteResult from './SiteResult';
import { dateToyymmddFormat, addDays, isDateNotBetweenTwoDates, sleep, processSiteResults } from '../../../assets/js/utils.js';
//custom scripts
import { search, fetchFormFieldData } from '../../../assets/js/api.js';
import { bus } from '../../../main';
import { mapMutations, mapActions } from 'vuex';
import { downloadFile, createScheduledReportQuery } from '../../../assets/js/api.js';

export default {
  name: 'Site',
  components: {
    SlowQueryModal,
    ScheduleReportModal,
    SiteResult,
    DateSiteModal,
    SiteModal,
    TargetGroupSiteModal
  },
  data() {
    return {
      allColsSelected: false,
      goToListTab: false,
      showScheduleReportModal: false,
      loadingScheduleReport: false,
      loadingExportResults:false,
      showSlowQueryModal: false,
      noMeasuresSelected: true,
      loadingFormData: true,
      loadingFormDataValue: 0,
      loadingFormDataMaxValue: 3,
      loadingSearchResults: false,
      initFirstSearch: false,
      formValidator: {
        dateFrom: true,
        dateTo: true,
      },
      form: {
        date: {
            datePicker: {
              weekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
              months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          },
          showDateSiteModal: false
        },
        columns: {
          options: [],
        },
        sites: {
          options: [],
          selected: [],
          showSiteModal: false,
          apiBodyGetter: "siteApiBody/getPayload",
          apiBodySetter: "siteApiBody/updatePayload"
        },
        target_group: {
          measurements: {
            options: [],
            selected: '',
          },
          showTargetGroupModal: false,
        }
      },
      selectedBtns: []
    };
  },
  computed: {
    allModalsClosed() {
      let allModalsClosed = false;
      if (
        !this.form.date.showDateSiteModal &&
        !this.form.sites.showSiteModal &&
        !this.form.target_group.showTargetGroupModal
      )
        allModalsClosed = true;
      return allModalsClosed;
    },
     disableRunBtn() {
       let disableRunBtn = false;
       let chosenCols = this.$store.state.siteApiBody.payload.columns;
       if (this.loadingSearchResults || chosenCols.length < 2) disableRunBtn = true;
       return disableRunBtn;
     },
    hasData() {
      return this.$store.state.siteResults.results.length > 0;
    },
    selectedColumns: {
      get() {
        return this.$store.state.siteApiBody.payload.columns.filter(c => c !== "site_id");
      },
      set(val) {
        // commits with requiredColumns ("site_id") appended in the mutation
        this.$store.commit('siteApiBody/updateColumns', val);
      }
    },
    start_date: {
      get() {
        let startDate = this.$store.state.siteApiBody.payload.consumption_start_date;
        //setting date when no date is set
        if (!startDate && this.$store.state.systemInfo.publishedToDate) {
          let newStartDate = addDays(this.$store.state.systemInfo.publishedToDate, -2);
          this.$store.commit('siteApiBody/updateStartDate', newStartDate);
          return newStartDate;
        }
        //setting date from clicking the calendar in the interface.
        else return startDate;
      },
      set(value) {
        this.$store.commit('siteApiBody/updateStartDate', value);
      },
    },
    end_date: {
      get() {
        let endDate = this.$store.state.siteApiBody.payload.consumption_end_date;
        //setting date when no date is set
        if (!endDate) {
          this.$store.commit(
            'siteApiBody/updateEndDate',
            this.$store.state.systemInfo.publishedToDate,
          );
          return this.$store.state.systemInfo.publishedToDate;
        }
        //setting date from clicking the calendar in the interface.
        return endDate;
      },
      set(value) {
        this.$store.commit('siteApiBody/updateEndDate', value);
      },
    },
    payload(){
      return this.$store.getters["siteApiBody/getPayload"]
    },
    getSelectedInputs() {
      let selectedInputs = this.selectedBtns.length;
      return selectedInputs;
    },
  },
  watch: {
    "$store.state.systemInfo.publishedToDate"(pubToDate){
      this.setDefaultStartDateEndDate(pubToDate)
    },
    payload: {
      handler() {
        let query = this.$store.state.siteApiBody.payload
        let selectedBtns = [];
        selectedBtns.push(
          {
            text: `<span class="font-weight-bold">Consumption date:</span> ${query.consumption_start_date} - ${query.consumption_end_date}`,
            editable: false,
            clickedBtn: () => {
              this.form.date.showDateSiteModal = true;
            }
          })
        if (query.site_id) {
          let chosenText = query.site_id.length > 1 ? "selected" : "selected";
          selectedBtns.push({
            text: `<span class="font-weight-bold">Site:</span> (${query.site_id.length} ${chosenText})`,
            clickedBtn: () => {
              this.form.sites.showSiteModal = true;
            }
          });
        }
        let numberOfStoredTargets = this.getTargetGroupCount();
        if (numberOfStoredTargets > 0) {
          let chosenText = numberOfStoredTargets > 1 ? "selected" : "selected";
          selectedBtns.push({
            text: `<span class="font-weight-bold"> Target / Metric </span> ${numberOfStoredTargets} ${chosenText}`,
            editable: true,
            clickedBtn: () => {
              this.form.target_group.showTargetGroupModal = true;
            },
            clickedBtnDelete: event => {
              event.stopPropagation();
              this.goToListTab = true;
              this.$store.commit('siteApiBody/updateMeasures', []);
            }
          });
        }
        this.selectedBtns = selectedBtns;
      },     
      deep: true
    },
  },
  beforeDestroy() {
    // Clean up by removing the event listener when the component is destroyed
    document.removeEventListener("keyup", this.onKeyUp);
  },
  async mounted() {
    if (!this.$store.state.systemInfo.fullAccess) {
      this.$router.push('/');
    }
    this.$store.commit('systemInfo/updateAnyFormFieldsLoaded', true);
    document.addEventListener("keyup", this.onKeyUp);
  },
  async created() {
    //Fetch sites from api
    let sitesResponse = await fetchFormFieldData('Sites', 'Site');
    if (sitesResponse.statusCode) {
      this.$bvToast.toast(sitesResponse.errorMessage, {
        title: 'Warning',
        variant: 'warning',
        solid: true,
        autoHideDelay: 3000,
      });
    } else {
      for (let index in sitesResponse) {
        let site = {
          text: sitesResponse[index].site_name,
          value: sitesResponse[index].site_id,
          selected: true,
        };
        this.form.sites.options.push(site);
      }
      this.loadingFormDataValue = 66;
    }
    let siteValues = this.form.sites.options.map(s => s.value)
    this.updateSiteIds(siteValues);
    //Fetch measures types from api
    let measureTypeResponse = await fetchFormFieldData('MeasureTypes', 'Site');
    if (measureTypeResponse.statusCode) {
      this.$bvToast.toast(measureTypeResponse.errorMessage, {
        title: 'Warning',
        variant: 'warning',
        solid: true,
        autoHideDelay: 3000,
      });
    } else {
      for (let index in measureTypeResponse) {
        let measure = {
          text: measureTypeResponse[index].display_name,
          value: measureTypeResponse[index].measure_type,
          showReachSelector: measureTypeResponse[index].show_reach_selector,
          ...measureTypeResponse[index].show_reach_selector && {
            reachAbsHeader: measureTypeResponse[index].reach_abs_header,
            reachRelHeader: measureTypeResponse[index].reach_rel_header,
          },
        };
        this.form.target_group.measurements.options.push(measure);
      }
      this.form.target_group.measurements.selected = measureTypeResponse[0].measure_type;
      let columnsSelected = [];
      let columnsResponse = await fetchFormFieldData('ColumnTypes/SiteColumns');
      for (let index in columnsResponse) {
        let defaultSelected =
            columnsSelected.length > 0
              ? columnsSelected.includes(columnsResponse[index].column_type)
              : columnsResponse[index].default_selection;
          let column = {
            text: columnsResponse[index].display_name,
            value: columnsResponse[index].column_type,
            defaultSelected,
          };
          this.form.columns.options.push(column);
      }
      this.updateColumnsApiBody();
      this.loadingFormDataValue = 100;
    }
    let pubDate = this.$store.state.systemInfo.publishedToDate
    if(pubDate)
      this.setDefaultStartDateEndDate(pubDate);
    // Sleep so that the progress bar has time to finnish
    await sleep(600);
    if(!this.$store.state.siteApiBody.hasSetDefaultSitePayload && this.$route.meta.tab === 'Sites'){
      this.$store.commit('siteApiBody/setDefaultSitePayload', this.$store.state.siteApiBody.payload);
    }
    this.loadingFormData = false;
  },
  methods: {
    ...mapMutations('siteReachApiBody', ['emptyQueryKeys']),
    ...mapActions(['resetReachSiteState']),
    toggleAll() {
      this.$store.commit("siteApiBody/updateColumns", this.allColsSelected ? this.form.columns.options.map(a => a.value) : []);
    },
    updateColumnsApiBody() {
      let selectedCols = this.form.columns.options.filter(c => c.defaultSelected).map(c => c.value)
      this.$store.commit('siteApiBody/updateColumns', selectedCols);
    },
    async handleScheduleReportSubmit(reportname) {
      this.loadingScheduleReport = true
      try {
          let payload = {...this.$store.state.siteApiBody };
          
          // kör frågan här
          await createScheduledReportQuery(reportname,payload);

          this.$bvToast.toast("The run is scheduled, you will receive an email when it is done.", {
              title: "Scheduled run",
              variant: "success",
              solid: true,
              autoHideDelay: 5000,
          })
      } catch (error) {
          this.loadingScheduleReport = false
          this.showScheduleReportModal = false
          this.$bvToast.toast("Could not schedule the report run", {
              title: "Server error",
              variant: "danger",
              solid: true,
              autoHideDelay: 5000,
          })
      } finally {
          this.loadingScheduleReport = false
          this.showScheduleReportModal = false
      }


    },
    async handleExportResults() {
      try {
        this.loadingExportResults = true;
        let payload = { ...this.$store.state.siteApiBody };
        await downloadFile('QuerySiteData/DownloadExport', payload);
      } catch (error) {
        console.error('Failed to export results:', error);
        this.$bvToast.toast('An error occurred while exporting data. Please try again later.', {
          title: 'Export failed', 
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loadingExportResults = false;
        this.showSlowQueryModal = false;
      }
    },

    getTargetGroupCount() {
      let storedTargets = this.payload.measures;

      return storedTargets != null
        ? storedTargets.length
        : 0;
    },
    clearAllFields(){

        //reset dates
        this.$store.commit('siteApiBody/updateEndDate',this.$store.state.systemInfo.publishedToDate);

        let newStartDate = addDays(this.$store.state.systemInfo.publishedToDate, -2);
        
        this.$store.commit('siteApiBody/updateStartDate', newStartDate);
        //reset sites
        let siteValues = this.form.sites.options.map(s => s.value)
        this.updateSiteIds(siteValues);
        this.$store.commit('siteApiBody/updateMeasures', []);
    },
    isFromDateDisabled(date) {
      let disableDate = isDateNotBetweenTwoDates(
        date,
        new Date(this.$store.state.systemInfo.publishedFromDate),
        new Date(this.$store.state.systemInfo.publishedToDate),
      );
      return disableDate;
    },
    isEndDateDisabled(date) {
      let disableDate = isDateNotBetweenTwoDates(
        date,
        new Date(this.$store.state.systemInfo.publishedFromDate),
        new Date(this.$store.state.systemInfo.publishedToDate),
      );
      return disableDate;
    },
    formatDate(date) {
      return dateToyymmddFormat(date);
    },
    setDefaultStartDateEndDate(pubDate){
      let newStartDate = addDays(pubDate, -2);
      this.$store.commit('siteApiBody/updateStartDate', newStartDate);
      this.$store.commit("siteApiBody/updateEndDate", pubDate);
    },
    updateSiteIds(value) {
      this.$store.commit('siteApiBody/updateSiteIds', value);
    },
    async submit() {
      if (
        this.$store.state.siteApiBody.payload.consumption_start_date >
        this.$store.state.siteApiBody.payload.consumption_end_date
      ) {
        this.$bvToast.toast('From-date cannot be greater than to-date.', {
          title: 'Invalid search period',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
          bodyOutputType: 'trustedHtml',
        });
        return;
      }
      if (
        this.$store.state.siteApiBody.payload.consumption_start_date <
        this.$store.state.systemInfo.publishedFromDate
      ) {
        this.$bvToast.toast(
          'The period cannot start before ' + this.$store.state.systemInfo.publishedFromDate + '.',
          {
            title: 'Invalid search period',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000,
            bodyOutputType: 'trustedHtml',
          },
        );
        return;
      }
      if (
        this.$store.state.siteApiBody.payload.consumption_end_date >
        this.$store.state.systemInfo.publishedToDate
      ) {
        this.$bvToast.toast(
          'To-date cannot be greater than the latest publication date which is ' +
            this.$store.state.systemInfo.publishedToDate +
            '.',
          {
            title: 'Invalid search period',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000,
            bodyOutputType: 'trustedHtml',
          },
        );
        return;
      }
      if (this.getTargetGroupCount() === 0) return;
      if (
        this.$store.state.siteApiBody.payload.consumption_start_date.length > 0 &&
        this.$store.state.siteApiBody.payload.consumption_end_date.length > 0
      ) {
        this.formValidator.dateFrom = true;
        this.formValidator.dateTo = true;
        this.initFirstSearch = true;
        this.loadingSearchResults = true;
        let apiBody = this.$store.state.siteApiBody;
        bus.$emit('searching', 713);

        if (!this.$store.state.siteApiBody.isOnSortSearch) {
          this.$store.commit('siteApiBody/updateSortBy', 'measure_1');
          this.$store.commit('siteApiBody/updateSortDirection', 'DESC');
        }

        // eslint-disable-next-line no-console
        let siteResults = null;
        this.resetReachSiteState();

        this.$store.commit('siteApiBody/updateExcludedRows', []);
        this.$store.commit('siteApiBody/updateSelectedColumns', this.selectedColumns);

        this.$store.commit('siteResults/updateResults', []);
        this.$store.commit('siteResults/updateResultFields', []);

// get querycost
        
      await search('QuerySiteData/QueryCost', apiBody)
            .then(async queryCost => {
              if(queryCost.queryClass == 'Normal') {
                        await search('QuerySiteData', apiBody)
                          .then((response) => {
                            this.$store.commit('siteResults/updateRowCount', response.info.total_row_count);
                            siteResults = response;
                            this.emptyQueryKeys();
                            processSiteResults(siteResults, this.$store);
                          })
                          .catch(() => {
                            this.loadingSearchResults = false;
                            this.$bvToast.toast('Unknown server error when fetching search results', {
                              title: 'Error',
                              variant: 'danger',
                              solid: true,
                              autoHideDelay: 10000,
                            });
                            this.$store.commit('siteResults/updateResults', []);
                            this.$store.commit('siteResults/updateResultFields', []);
                            return;
                          });
            } else if (queryCost.queryClass == 'RowLimitExceeded') {
                  this.loadingSearchResults = false;
                  this.$store.commit('searchResults/updateResults', []);
                  this.$store.commit('searchResults/updateResultFields', []);
                  this.showSlowQueryModal = true;
                  return;

              } else if (queryCost.queryClass == 'Schedule') {
                      this.loadingSearchResults = false;
                      this.$store.commit('searchResults/updateResults', []);
                      this.$store.commit('searchResults/updateResultFields', []);
                      this.showScheduleReportModal = true;
                      return;

              } else if (queryCost.queryClass == 'Forbidden') {
                      this.loadingSearchResults = false;
                      this.$bvToast.toast('The run is too extensive', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true,
                        autoHideDelay: 10000,
                      });
                      this.$store.commit('searchResults/updateResults', []);
                      this.$store.commit('searchResults/updateResultFields', []);
                      return;
              } 
          })


        this.loadingSearchResults = false;
      } else {
        this.formValidator.dateFrom = this.$store.state.siteApiBody.payload.start_date.length > 0;
        this.formValidator.dateTo = this.$store.state.siteApiBody.payload.end_date.length > 0;
      }
    },
    onKeyUp(event) {
      if (event.key === "Enter" && this.allModalsClosed) {
        this.submit();
      }
    },
    async submitSort() {
      this.loadingSearchResults = true;
      const apiBody = this.$store.state.siteApiBody;
      
      // Store current selections before the sort
      const selectedRows = this.$store.state.siteResults.results
        .filter(item => item.valda)
        .map(item => item.rowNum);
      
      try {
        const response = await search('QuerySiteData', apiBody);
        this.$store.commit('siteResults/updateRowCount', response.info.total_row_count);
        
        // Process results but preserve selections
        processSiteResults(response, this.$store);
        
        // Restore selections based on rowNum
        selectedRows.forEach(rowNum => {
          this.$store.commit('siteResults/select', rowNum);
        });
        
      } catch (error) {
        this.$bvToast.toast('Unknown server error when fetching search results', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 10000,
        });     
        this.$store.commit('siteResults/updateResults', []);
        this.$store.commit('siteResults/updateResultFields', []);
      } finally {
        this.loadingSearchResults = false;
      }
    },
    async handleExcludeRows(rows) {
      this.loadingSearchResults = true;
      const apiBody = this.$store.state.siteApiBody;
      
      // Update excluded rows in store
      this.$store.commit('siteApiBody/updateExcludedRows', rows);
      
      // Store current total row count before the update
      const previousTotalRowCount = this.$store.state.siteResults.totalRowCount;
      
      try {
        const response = await search('QuerySiteData', apiBody);
        
        // Update total row count to maintain excluded rows state
        this.$store.commit('siteResults/updateRowCount', previousTotalRowCount);
        
        // Process results but keep the total row count
        processSiteResults(response, this.$store);
        
      } catch (error) {
        this.$bvToast.toast('Unknown server error when fetching search results', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 10000,
        });     
        this.$store.commit('siteResults/updateResults', []);
        this.$store.commit('siteResults/updateResultFields', []);
      } finally {
        this.loadingSearchResults = false;
      }
    },
  },
};
</script>

<style scoped>
.wildcardCbxText {
  font-size: small;
}
.supportTextNoAnswers {
  font-size: 0.8em;
  font-style: italic;
}
>>> .dropdown-toggle {
  width: 10rem;
  color: black;
  border-color: rgb(159 157 156 / var(--tw-border-opacity));
  --tw-border-opacity: 1;
  --tw-bg-opacity: 1;
  box-shadow: none;
  outline: none;
}
>>> .dropdown-toggle:hover,
>>> .dropdown-toggle:active,
>>> .dropdown-toggle:focus-visible,
>>> .show > .dropdown-toggle {

  background-color: rgb(139 201 255 / var(--tw-bg-opacity)) !important;
  border-color: rgb(0 147 255 / var(--tw-border-opacity)) !important;
  color: black !important;
}
.aligntextmiddle{
  color: #495057;
  vertical-align: sub;
  font-size: 1.05em;
}
.checkboxwrapper{
  display: flex;
  flex-direction: column;
  margin-left: .7rem;
}
</style>
